import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import Panel from "./Panel";
import {
  Dimmer,
  Input,
  Loader,
  Message,
  Segment,
  Table,
  Pagination,
  Icon,
  Dropdown,
} from "semantic-ui-react";
import { searchOrders } from "../libs/requests/orderSearch";
import { fetchVodafoneOrders } from "../libs/requests/vodafoneOrderSearch";
import { Link } from "react-router-dom";
import { authContext } from "../containers/AuthProvider";
import moment from "moment-timezone";
import { Alert } from "react-bootstrap";
import updatePartnerAsset from "../libs/requests/updatePartnerAsset";
import { getPartners } from "../libs/requests/getPartnerDetails";
import { useLoading, useOrders } from "./customHooks";
import { getSupplier, supplierKey } from "../libs/sessionVariable";

let timeout = null;

export default function SearchOrders() {
  const [isLoading, setIsLoading] = useLoading(true);
  const [isError, setIsError] = useState(false);
  const [orders, setOrders] = useOrders([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [reloadPage, setReloadPage] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [fulfilmentState, setFulfilmentState] = useState(71);

  const [page, setPage] = useState({
    index: 0,
    size: 10,
    count: 1,
    data: [],
  });
  const [info, setInfo] = useState({});
  const { permissions } = useContext(authContext);
  const [isHightide, setIsHightide] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("zen");

  const highTidePartners = [
    { Tenant: "Faelix", Partner: "OneHelix", Realms: [""] },
    { Tenant: "Faelix", Partner: "E-volve Solutions", Realms: [""] },
    { Tenant: "Faelix", Partner: "IT4 Automation", Realms: [""] },
    { Tenant: "Faelix", Partner: "Community Waves", Realms: [""] },
    { Tenant: "Faelix", Partner: "Hightide", Realms: [""] },
    { Tenant: "Faelix", Partner: "Netix", Realms: [""] },
    { Tenant: "Faelix", Partner: "Countryside Connections", Realms: [""] }
  ];

  const assignedToOptions = useMemo(() => {
    const assignedTos = new Set();
    orders.forEach((order) => {
      if (order.assignedTo) {
        assignedTos.add(order.assignedTo);
      }
    });

    return [
      { key: "all", text: "All", value: "" },
      ...Array.from(assignedTos).map((value) => ({
        key: value,
        text: value,
        value: value,
      })),
    ];
  }, [orders]);

  const fulfilmentStateOptions = [
    { key: "all", value: 0, text: "All States" },
    { key: "pending", value: 1, text: "Pending" },
    { key: "acknowledged", value: 2, text: "Acknowledged" },
    { key: "committed", value: 4, text: "Committed" },
    { key: "completed", value: 8, text: "Completed" },
    { key: "rejected", value: 16, text: "Rejected" },
    { key: "cancelled", value: 32, text: "Cancelled" },
    { key: "delayed", value: 64, text: "Delayed" },
    { key: "inFlight", value: 71, text: "In Flight" },
    { key: "cancelling", value: 128, text: "Cancelling" },
  ];

  const supplierOptions = [
    { key: "zen", text: "ZEN", value: "zen" },
    { key: "vodafone", text: "Vodafone", value: "vodafone" },
  ];

  useEffect(() => {
    if (!assignedToFilter && !searchTerm) {
      setFilteredOrders([...orders]);
    }
  }, [assignedToFilter, searchTerm, orders]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        if (supplierFilter === "vodafone") {
          const result = await fetchVodafoneOrders(searchTerm);
          const normalized = (Array.isArray(result) ? result : []).map((o) => ({
            reference: o.Reference,
            orderDate: o.ActivationDate,
            provisionType: o.Status,
            customerReference: "",
            productDescription: o.ProductDescription,
            phoneNumber: o.Address,
          }));
          setOrders(normalized);
        } else {
          const result = await searchOrders(searchTerm, fulfilmentState);
          setOrders(result?.orders || []);

          if (permissions.admin) {
            const supplier = await getSupplier("", true);
            setIsHightide(supplier === "Faelix");
            setPartners(
              supplier === "Faelix"
                ? highTidePartners
                : (await getPartners()).map((p) => ({
                    Tenant: "",
                    Partner: p,
                    Realms: [""],
                  }))
            );
          }
        }

        setIsLoading(false);
      } catch (err) {
        setIsError(true);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fulfilmentState, searchTerm, reloadPage, supplierFilter]);

  useEffect(() => {
    if (orders.length > 0) {
      setIsFiltering(true);

      const filtered = orders.filter((order) => {
        const matchesSearch =
          !searchTerm ||
          order?.reference?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          order?.customerReference
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase());

        const matchesPartner =
          !assignedToFilter ||
          (order.assignedTo &&
            order.assignedTo
              ?.toLowerCase()
              ?.includes(assignedToFilter?.toLowerCase()));

        return matchesSearch && matchesPartner;
      });

      setFilteredOrders(filtered);
      setIsFiltering(false);
      buildPage(1);
    }
  }, [orders, searchTerm, assignedToFilter]);

  useEffect(() => {
    buildPage(1);
  }, [filteredOrders]);

  const buildPage = (pageIndex) => {
    const { size } = page;
    const count = filteredOrders.length;
    const noPages = Math.ceil(count / size);
    const startIdx = (pageIndex - 1) * size;

    setPage({
      ...page,
      index: pageIndex,
      count: noPages,
      data: filteredOrders.slice(startIdx, startIdx + size),
      isFirst: pageIndex === 1,
      isLast: pageIndex === noPages,
    });
  };

  const handleSearchChange = (evt) => {
    const term = evt.target.value;
    setSearchTerm(term);
  };

  const handleAssignedToFilterChange = (e, { value }) => {
    setAssignedToFilter(value);
  };

  const handleFulfilmentStateChange = (e, { value }) => {
    setFulfilmentState(value);
  };

  const handleSupplierChange = (e, { value }) => {
    setSupplierFilter(value);
    setSearchTerm("");
  };

  const debouncedFilterOrders = useMemo(() => {
    return debounce(async (searchTerm, assignedToTerm) => {
      setIsSearching(true);

      try {
        const sourceOrders = searchTerm
          ? (await searchOrders(searchTerm, fulfilmentState)).orders
          : [...orders];

        const filtered = sourceOrders.filter((order) => {
          const matchesSearch =
            !searchTerm ||
            order?.reference?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            order.customerReference
              ?.toLowerCase()
              ?.includes(searchTerm?.toLowerCase());

          const matchesAssignedTo =
            !assignedToTerm ||
            (order.assignedTo &&
              order.assignedTo
                ?.toLowerCase()
                ?.includes(assignedToTerm?.toLowerCase()));

          return matchesSearch && matchesAssignedTo;
        });

        setFilteredOrders(filtered);
        buildPage(1);
      } catch (err) {
        console.error("Error filtering orders:", err);
        setIsError(true);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [orders, fulfilmentState]);

  const handlePaginationChange = (e, { activePage }) => {
    buildPage(activePage);
  };

  const assign = async (reference, partner) => {
    try {
      // Show loading state
      setInfo({
        type: "info",
        text: `Assigning ${reference} to ${partner}...`,
      });

      // Prepare the partner name based on supplier
      const formattedPartner = isHightide ? `Hightide|${partner}` : partner;

      // Determine supplier based on reference prefix
      let supplier;
      if (reference.toUpperCase().startsWith("Z")) {
        supplier = "ZEN";
      } else if (reference.toUpperCase().startsWith("ORD")) {
        supplier = "GIGANET";
      } else {
        setInfo({
          type: "warning",
          text: `Invalid reference format for ${reference}.`,
        });
        return;
      }

      // Make the API call
      const result = await updatePartnerAsset({
        reference,
        partner: formattedPartner,
        supplier,
      });

      // Handle the response
      if (result) {
        setInfo({
          type: "success",
          text: `Assigned ${reference} to ${partner}. Refreshing data...`,
        });

        // Reset search and trigger data refresh
        setSearchTerm("");
        setIsRefreshing(true);
        setReloadPage((prev) => !prev);
      } else {
        setInfo({
          type: "warning",
          text: `Failed to assign ${reference} to ${partner}`,
        });
      }
    } catch (error) {
      setInfo({
        type: "danger",
        text: `Error assigning ${reference}: ${error.message}`,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  if (
    !isLoading &&
    (!permissions.orderAccess ||
      !permissions ||
      (permissions && !permissions.hasOwnProperty("admin")))
  ) {
    return (
      <Panel>
        <Message error header="You don't have permission to view this page" />
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel>
        <Message
          error
          header="There was an error searching for orders, please check and try again"
        />
      </Panel>
    );
  }

  const { data, index, isFirst, isLast, count } = page;

  return (
    <>
      <Panel header={"Orders"}>
        {!!info.text && <Alert bsStyle={info.type}>{info.text}</Alert>}

        <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
          <Input
            data-testid="ordersSearchPanel"
            icon={
              searchTerm
                ? {
                    name: "close",
                    link: true,
                    onClick: () => setSearchTerm(""),
                  }
                : "search"
            }
            placeholder="Search Orders..."
            loading={isLoading || isFiltering}
            size="large"
            onChange={handleSearchChange}
            style={{ flex: 2 }}
            value={searchTerm}
          />

          {permissions.isVodafoneOnboarded && (
            <Dropdown
              placeholder="Supplier"
              data-testid="supplierFilter"
              fluid
              selection
              options={supplierOptions}
              onChange={handleSupplierChange}
              value={supplierFilter}
              style={{ flex: 1 }}
            />
          )}

          {supplierFilter !== "vodafone" && (
            <Dropdown
              placeholder="Select State"
              data-testid="stateFilter"
              fluid
              selection
              options={fulfilmentStateOptions}
              onChange={handleFulfilmentStateChange}
              value={fulfilmentState}
              style={{ flex: 1 }}
              loading={isLoading}
            />
          )}

          {permissions.admin && (
            <Dropdown
              placeholder="Filter by Partner"
              data-testid="assignedToFilter"
              fluid
              search
              selection
              clearable
              options={assignedToOptions}
              onChange={handleAssignedToFilterChange}
              value={assignedToFilter}
              style={{ flex: 1 }}
              loading={isFiltering}
            />
          )}
        </div>

        <Segment style={{ minHeight: "100px", width: "100%" }}>
          <Dimmer active={isLoading || isFiltering} inverted>
            <Loader inverted>
              {isLoading ? "Loading orders..." : "Filtering..."}
            </Loader>
          </Dimmer>

          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Reference</Table.HeaderCell>
                <Table.HeaderCell>Placed Date</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Customer Reference</Table.HeaderCell>
                <Table.HeaderCell>Product</Table.HeaderCell>
                <Table.HeaderCell>CLI</Table.HeaderCell>
                {permissions.admin && (
                  <Table.HeaderCell>Assigned To</Table.HeaderCell>
                )}
                {permissions.admin && (
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>

            {!!filteredOrders.length && (
              <>
                <Table.Body>
                  {data.map((order, index) => (
                    <Table.Row
                      key={`${order?.reference}-${index}-${order?.orderDate}`}
                    >
                      <Table.Cell>
                        {supplierFilter === "vodafone" ? (
                          order.reference
                        ) : (
                          <Link
                            to={{
                              pathname: `/Order/${order?.reference}`,
                              search: `?fulfilmentState=${fulfilmentState}${
                                order?.orderDate
                                  ? `&orderDate=${encodeURIComponent(
                                      order.orderDate
                                    )}`
                                  : ""
                              }`,
                            }}
                          >
                            {order.reference}
                          </Link>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {moment(order.orderDate).format("DD-MM-YY")}
                      </Table.Cell>
                      <Table.Cell>{order.provisionType}</Table.Cell>
                      <Table.Cell>{order.customerReference}</Table.Cell>
                      <Table.Cell>{order.productDescription}</Table.Cell>
                      <Table.Cell>{order.phoneNumber}</Table.Cell>
                      {permissions.admin && (
                        <Table.Cell data-testid="assigned-to-cell">
                          {order.assignedTo}
                        </Table.Cell>
                      )}

                      {permissions.admin && (
                        <Table.Cell>
                          <Dropdown
                            text="Assign"
                            fluid
                            button
                            className="icon"
                            scrolling
                            disabled={isLoading}
                            style={{
                              minWidth: "150px",
                              position: "relative",
                            }}
                          >
                            <Dropdown.Menu
                              style={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                minWidth: "250px",
                                position: "absolute",
                                left: 0,
                                zIndex: 1001,
                                marginTop: "5px",
                              }}
                            >
                              {partners.map((o) => (
                                <Dropdown.Item
                                  key={o.Partner}
                                  onClick={() =>
                                    assign(order.reference, o.Partner)
                                  }
                                  style={{
                                    padding: "10px 15px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {o.Partner}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </>
            )}
          </Table>

          {!!filteredOrders.length && (
            <Pagination
              activePage={index}
              onPageChange={handlePaginationChange}
              totalPages={count}
              firstItem={null}
              lastItem={null}
              prevItem={{
                content: <Icon name="angle left" disabled={isFirst} />,
                icon: true,
              }}
              nextItem={{
                content: <Icon name="angle right" disabled={isLast} />,
                icon: true,
              }}
            />
          )}
        </Segment>
      </Panel>
    </>
  );
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}
